import React, { useState, useRef, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Link } from 'react-router-dom';
import { Tooltip, OverlayTrigger } from 'react-bootstrap';
import { useAuth } from '../../contexts/AuthContext';
import { useOnClickOutside, useWindowWidth } from '../../utils';
import ProjectCell from '../admin/ProjectCell';
import Logo from '../../assets/images/logo.png';
import questionImg from '../../assets/images/question.svg';
import caretImg from '../../assets/images/caret.svg';
import avatarImg from '../../assets/images/avatar.png';
import plusImg from '../../assets/images/plus.svg';
import closeImg from '../../assets/images/close.svg';
import menuImg from '../../assets/images/menu.svg';
import { createProject, updateProject, deleteProject } from '../../actions/project';
import { deleteStakeholders } from '../../actions/stakeholder';
import { deleteConnections } from '../../actions/connection';
import firebase from '../../firebase';
import { NotificationManager } from 'react-notifications';
import Avatar from 'react-avatar';

const firestore = firebase.firestore();

const Header = () => {
  const width = useWindowWidth();
  const ref = useRef();
  const uploaderRef = useRef();
  const exceptional = useRef();
  const medicalRef = useRef();
  const medicalExceptional = useRef();
  const { currentUser, signout } = useAuth();

  const dispatch = useDispatch();

  const projects = useSelector((state) => state.project.projects);
  const connections = useSelector((state) => state.connection.connections);
  const stakeholders = useSelector((state) => state.stakeholder.stakeholders);
  const activeProject = useSelector((state) => state.project.project);
  const [newProjectName, setNewProjectName] = useState('');
  const [selected, setSelected] = useState('');
  const [showProjectList, setShowProjectList] = useState(false);
  const [isShowCreateProjectModal, setShowCreateProjectModal] = useState(false);
  const [isShowEditProjectModal, setShowEditProjectModal] = useState(false);
  const [isShowDeleteProjectModal, setShowDeleteProjectModal] = useState(false);
  const [profile, setProfile] = useState('');
  const [isShowEditProfileModal, setShowEditProfileModal] = useState(false);
  const [showMenuActions, setShowMenuActions] = useState(false);

  useOnClickOutside(ref, () => setShowMenuActions(false), exceptional);
  useOnClickOutside(medicalRef, () => setShowProjectList(false), medicalExceptional);

  useEffect(() => {
    const userRef = firestore.collection('users').doc(currentUser.uid);

    userRef
      .get()
      .then((doc) => {
        if (doc.exists) {
          setProfile(doc.data());
        } else {
          console.log('No such user');
        }
      })
      .catch((error) => console.log(error));
  }, []);

  const toggleProductList = () => {
    setShowProjectList(!showProjectList);
  };

  const handleShowCreateProjectModal = () => {
    setShowProjectList(false);
    setShowCreateProjectModal(true);
  };

  const handleCreateProject = () => {
    if (!newProjectName) return;
    dispatch(
      createProject({
        userId: currentUser.uid,
        name: newProjectName,
        createdAt: new Date(),
      }),
    );

    setNewProjectName('');
    setShowCreateProjectModal(false);
  };

  const handleEditProject = () => {
    if (!selected.name) return;
    dispatch(
      updateProject({
        ...selected,
        updatedAt: new Date(),
      }),
    );
    setShowEditProjectModal(false);
  };

  const handleDeleteProject = () => {
    dispatch(deleteProject(selected.id));

    let filteredStakeholderIds = stakeholders
      .filter((c) => {
        return c.projectId === selected.id;
      })
      .map((c) => c.id);
    dispatch(deleteStakeholders(filteredStakeholderIds));

    let filteredConnectionIds = connections
      .filter((c) => {
        return c.projectId === selected.id;
      })
      .map((c) => c.id);
    dispatch(deleteConnections(filteredConnectionIds));

    setShowDeleteProjectModal(false);
  };

  const handleEditProfile = () => {
    firestore
      .collection('users')
      .doc(currentUser.uid)
      .set(profile)
      .then(() => {
        NotificationManager.success('The profile has been updated successfully', 'Success!');
      })
      .catch((error) => {
        NotificationManager.error('Profile update failed', 'Failure!');
      });
    setShowEditProfileModal(false);
  };

  const handleLogout = () => {
    signout();
  };

  const renderTooltip = (props) => (
    <Tooltip id="custom-tooltip" {...props}>
      <div className="custom-tooltip">
        <div className="tooltip-header">Project Name!</div>
        <div className="tooltip-content">Here you can create, update, or delete projects.</div>
      </div>
    </Tooltip>
  );

  const handleUploadAvatar = (e) => {
    const file = uploaderRef.current.files[0];
    var storage = firebase.storage();
    var storageRef = storage.ref();
    var uploadTask = storageRef.child('avatars/' + file.name).put(file);

    uploadTask.on(
      firebase.storage.TaskEvent.STATE_CHANGED,
      (snapshot) => {
        var progress = Math.round(snapshot.bytesTransferred / snapshot.totalBytes) * 100;
        console.log('upload progress...', progress);
      },
      (error) => {
        throw error;
      },
      () => {
        uploadTask.snapshot.ref.getDownloadURL().then((url) => {
          console.log('upload complete', url);
          setProfile({ ...profile, avatar: url });
        });
      },
    );
  };

  return (
    <>
      <div className="header">
        <div className="logo-section">
          <a href="/">
            <img src={width > 600 ? Logo : menuImg} alt="logo" />
          </a>
        </div>
        <div className="right-section">
          <div className="project-selection" onClick={toggleProductList} ref={medicalExceptional}>
            <p>{activeProject ? activeProject.name : 'Projects'}</p>
            <img src={caretImg} alt="" className={showProjectList ? 'reverse' : ''} />
          </div>
          {showProjectList && (
            <div className="project-card" ref={medicalRef}>
              <div className="project-card-header">
                <div className="text-section">
                  <p>Projects List</p>
                  <OverlayTrigger
                    placement="bottom"
                    delay={{ show: 250, hide: 400 }}
                    overlay={renderTooltip}
                  >
                    <img src={questionImg} alt="quiz" />
                  </OverlayTrigger>
                </div>
                {width > 600 ? (
                  <button className="primary-btn" onClick={handleShowCreateProjectModal}>
                    <img src={plusImg} alt="plus" /> Create Project
                  </button>
                ) : (
                  <img src={closeImg} alt="" onClick={() => setShowProjectList(false)} />
                )}
              </div>
              <div className="project-list">
                {projects.map((project) => (
                  <ProjectCell
                    project={project}
                    activeProject={activeProject}
                    key={project.id}
                    setShowProjectList={setShowProjectList}
                    setSelected={setSelected}
                    setShowEditProjectModal={setShowEditProjectModal}
                    setShowDeleteProjectModal={setShowDeleteProjectModal}
                  />
                ))}
              </div>
              {width > 600 ? (
                ''
              ) : (
                <div className="mobile-create-project">
                  <button className="primary-btn" onClick={handleShowCreateProjectModal}>
                    <img src={plusImg} alt="plus" /> Create Project
                  </button>
                </div>
              )}
            </div>
          )}
          {width > 600 ? (
            <>
              <div className="avatar-section" ref={exceptional}>
                {profile.avatar ? (
                  <img
                    src={profile.avatar}
                    alt=""
                    onClick={() => setShowMenuActions(!showMenuActions)}
                  />
                ) : (
                  <Avatar
                    color={Avatar.getRandomColor(profile.firstName + ' ' + profile.lastName, [
                      'red',
                      'green',
                      'blue',
                    ])}
                    name={profile.firstName + ' ' + profile.lastName}
                    onClick={() => setShowMenuActions(!showMenuActions)}
                    size="40"
                  />
                )}
              </div>
              {showMenuActions && (
                <div className="menu-actions" ref={ref}>
                  {profile.role === 'admin' && (
                    <div className="action-item">
                      <Link to="/admin/users">Users</Link>
                    </div>
                  )}
                  <div className="action-item" onClick={() => setShowEditProfileModal(true)}>
                    <p>Edit Profile</p>
                  </div>
                  <div className="action-item" onClick={handleLogout}>
                    <p>Logout</p>
                  </div>
                </div>
              )}
            </>
          ) : (
            ''
          )}
        </div>
      </div>
      {isShowCreateProjectModal && (
        <div className="custom-modal">
          <div className="modal-card">
            <img
              src={closeImg}
              alt=""
              className="close-btn"
              onClick={() => setShowCreateProjectModal(false)}
            />
            <div className="modal-card-inner">
              <h1 className="title">Create Project</h1>
              <div className="form-item">
                <label htmlFor="project">Name for the Project</label>
                <input
                  type="text"
                  placeholder="Type your project name"
                  id="project"
                  value={newProjectName}
                  onChange={(e) => setNewProjectName(e.target.value)}
                />
              </div>
              <button className="primary-btn solo-btn" onClick={handleCreateProject}>
                Save
              </button>
            </div>
          </div>
        </div>
      )}
      {isShowEditProjectModal && (
        <div className="custom-modal">
          <div className="modal-card">
            <img
              src={closeImg}
              alt=""
              className="close-btn"
              onClick={() => setShowEditProjectModal(false)}
            />
            <div className="modal-card-inner">
              <h1 className="title">Edit Project</h1>
              <div className="form-item">
                <label htmlFor="type">Name for the Project</label>
                <input
                  type="text"
                  placeholder="Type your project name"
                  value={selected.name}
                  onChange={(e) => setSelected({ ...selected, name: e.target.value })}
                />
              </div>
              <button className="primary-btn solo-btn" onClick={handleEditProject}>
                Update
              </button>
            </div>
          </div>
        </div>
      )}

      {isShowDeleteProjectModal && (
        <div className="custom-modal">
          <div className="modal-card">
            <img
              src={closeImg}
              alt=""
              className="close-btn"
              onClick={() => setShowDeleteProjectModal(false)}
            />
            <div className="modal-card-inner">
              <h1 className="title">Are you sure, that you want to delete {selected.name} ?</h1>
              <div className="modal-actions">
                <button className="primary-btn" onClick={handleDeleteProject}>
                  Yes
                </button>
                <button className="outline-btn" onClick={() => setShowDeleteProjectModal(false)}>
                  No
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {isShowEditProfileModal && (
        <div className="custom-modal">
          <div className="modal-card">
            <img
              src={closeImg}
              alt=""
              className="close-btn"
              onClick={() => setShowEditProfileModal(false)}
            />
            <div className="modal-card-inner">
              <h1 className="title">Edit Profile</h1>
              <div className="form-item">
                <label htmlFor="type">Avatar</label>
                <div>
                  {profile.avatar ? (
                    <img className="avatar-image" src={profile.avatar} />
                  ) : (
                    <Avatar
                      color={Avatar.getRandomColor(profile.firstName + ' ' + profile.lastName, [
                        'red',
                        'green',
                        'blue',
                      ])}
                      name={profile.firstName + ' ' + profile.lastName}
                    />
                  )}
                  <button
                    className="primary-btn upload-btn"
                    onClick={() => uploaderRef.current.click()}
                  >
                    Upload/Change
                  </button>
                  <input
                    type="file"
                    ref={uploaderRef}
                    id="avatar-upload-input"
                    accept="image/*"
                    onChange={(e) => handleUploadAvatar(e)}
                  />
                </div>
              </div>
              <div className="form-item">
                <label htmlFor="type">First Name</label>
                <input
                  type="text"
                  placeholder="Type your first name"
                  value={profile.firstName}
                  onChange={(e) => setProfile({ ...profile, firstName: e.target.value })}
                />
              </div>
              <div className="form-item">
                <label htmlFor="type">Last Name</label>
                <input
                  type="text"
                  placeholder="Type your last name"
                  value={profile.lastName}
                  onChange={(e) => setProfile({ ...profile, lastName: e.target.value })}
                />
              </div>
              {/* <div className="form-item">
                <label htmlFor="type">Email</label>
                <input
                  type="text"
                  placeholder="Type your email"
                  value={profile.email}
                  onChange={(e) => setProfile({ ...profile, email: e.target.value })}
                />
              </div> */}
              <button className="primary-btn solo-btn" onClick={handleEditProfile}>
                Update
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default Header;
